/**
 * SSO Callback Handler Page (N6 cookie cutover, SEC-V04).
 *
 * After SSO authentication, aaim-core redirects here with a one-time, opaque
 * code on the query string. This page POSTs the code to
 * POST /api/v1/auth/exchange (credentials: 'include'); the backend consumes the
 * single-use code, sets the HttpOnly session cookies, and returns a body with
 * NO tokens. The page then hydrates the user from GET /api/v1/auth/me. The SPA
 * never receives a bearer token.
 *
 *   GET /sso/callback?code=<opaque>[&state=<return-url>]
 *
 * NOTE: the aaim-core SSO redirect that emits ?code= is the lead's atomic step;
 * this reader is authored ahead of it so aaim-core + all four institutional
 * SPAs flip together. Do not browser-test SSO until that flip lands.
 * See .claude/rules/no-browser-token-storage.md.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Heading, BodyText } from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';

export function SSOCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const errorCode = searchParams.get('error');
        if (errorCode) {
          throw new Error(searchParams.get('message') || 'SSO authentication failed');
        }

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        if (!code) {
          throw new Error('Missing authentication code');
        }

        // Exchange the one-time code for the HttpOnly session cookies. The
        // response body carries NO tokens; the session lives only in the cookie.
        const response = await fetch('/api/v1/auth/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          throw new Error('Sign-in could not be completed');
        }

        // Hydrate the user from the freshly-set cookie.
        const ok = await useAuthStore.getState().hydrateSession();
        if (!ok) {
          throw new Error('Sign-in succeeded but the session could not be loaded');
        }

        // Redirect to dashboard or the original return URL.
        const returnUrl = state ? decodeURIComponent(state) : '/';
        navigate(returnUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setProcessing(false);

        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    void handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="max-w-md mx-auto">
      <div className="mt-16 flex flex-col items-center text-center">
        {processing ? (
          <>
            <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mb-6" />
            <Heading level={2} size="section" className="font-sans mb-3">Completing Sign In</Heading>
            <BodyText className="text-secondary">
              Please wait while we complete your authentication...
            </BodyText>
          </>
        ) : (
          <>
            {error && (
              <Alert type="error" className="w-full mb-4">
                {error}
              </Alert>
            )}
            <BodyText className="text-secondary">
              Redirecting to login page...
            </BodyText>
          </>
        )}
      </div>
    </div>
  );
}
